import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  styled,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Collapse,
} from '@mui/material';
import { useWatch, useFormContext } from 'react-hook-form';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';

// ─── Layout helpers ───────────────────────────────────────────────────────────
const Row = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  gap: '20px',
}));

const Col = styled('div')(() => ({
  flex: 1,
}));
const ALLOWED_ADJ_TYPES = ["A", "NA", "p", "D", "O", "CG", "BZ", "BL"];
// ─── Per-type option lists ────────────────────────────────────────────────────
const ADJUSTED_OPTIONS = {
  answer: [
    { label: 'Not Answer', value: 'NA' },
    { label: 'Busy', value: 'BZ' },
    { label: 'Other', value: 'O' },
    { label: 'Congestion', value: 'CG' },
  ],
  unanswer: [
    { label: 'Answer', value: 'A' },
    { label: 'Busy', value: 'BZ' },
    { label: 'Other', value: 'O' },
    { label: 'Congestion', value: 'CG' },
  ],
  busy: [
    { label: 'Answer', value: 'A' },
    { label: 'Not Answer', value: 'NA' },
    { label: 'Other', value: 'O' },
    { label: 'Congestion', value: 'CG' },
  ],
  other: [
    { label: 'Answer', value: 'A' },
    { label: 'Not Answer', value: 'NA' },
    { label: 'Busy', value: 'BZ' },
    { label: 'Congestion', value: 'CG' },
  ],
};

const TYPE_CONFIG = {
  answer: {
    label: 'Answer',
    countField: 'answerCount',
    adjustedField: 'adjustedAnswerCount',
  },
  unanswer: {
    label: 'Not Answer',
    countField: 'notAnswerCount',
    adjustedField: 'adjustedNotAnswerCount',
  },
  busy: {
    label: 'Busy',
    countField: 'busyCount',
    adjustedField: 'adjustedBusyCount',
  },
  other: {
    label: 'Other',
    countField: 'otherCount',
    adjustedField: 'adjustedOtherCount',
  },
};

const COUNT_TYPES_ORDER = ['answer', 'unanswer', 'busy', 'other'];

// ─── Single count-type section ────────────────────────────────────────────────
function CountSection({ type, countMode }) {
  const { control } = useFormContext();
  const { label, countField, adjustedField } = TYPE_CONFIG[type];
  const options = ADJUSTED_OPTIONS[type];
  const isPercentage = countMode === 'percentage';
  const selectedAdjusted = useWatch({ control, name: adjustedField }) || [];

  return (
    <Row>
      <Col>
        <RHFTextField
          name={countField}
          label={`${label} ${isPercentage ? 'Percentage (%)' : 'Count'}`}
          type="number"
          fullWidth
          inputProps={{ min: 0, max: isPercentage ? 100 : undefined }}
        />
      </Col>
      <Col>
        <RHFSelect
          name={adjustedField}
          label={`Adjusted ${label} Count`}
          size="medium"
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              const labels = (selected || []).map(
                (v) => options.find((o) => o.value === v)?.label
              );
              return labels.length > 3
                ? `${labels.slice(0, 3).join(', ')} (+${labels.length - 3} More)`
                : labels.join(', ');
            },
          }}
        >
          {options.map(({ label: optLabel, value }) => (
            <MenuItem key={value} value={value}>
              <Checkbox checked={selectedAdjusted.includes(value)} size="small" />
              <ListItemText primary={optLabel} />
            </MenuItem>
          ))}
        </RHFSelect>
      </Col>
      {/* Empty third column to keep layout consistent */}
      <Col />
    </Row>
  );
}

// ─── Combined card ────────────────────────────────────────────────────────────
export default function CountUpdateCard({ activeTypes, countMode }) {
  return (
    <Box
      sx={{
        mt: 2,
        p: 3,
        borderRadius: 1,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Count Update
      </Typography>

      {COUNT_TYPES_ORDER.map((type, index) => {
        const isActive = activeTypes.includes(type);
        const isLast = index === COUNT_TYPES_ORDER.length - 1;

        return (
          <Box key={type}>
            <Collapse in={isActive} unmountOnExit>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  {TYPE_CONFIG[type].label}
                </Typography>
                <CountSection type={type} countMode={countMode} />
              </Box>
              {!isLast && isActive && activeTypes.some((t, i) => COUNT_TYPES_ORDER.indexOf(t) > COUNT_TYPES_ORDER.indexOf(type)) && (
                <Divider sx={{ mb: 2 }} />
              )}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

CountUpdateCard.propTypes = {
  activeTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  countMode: PropTypes.oneOf(['count', 'percentage']).isRequired,
};

CountSection.propTypes = {
  type: PropTypes.string.isRequired,
  countMode: PropTypes.oneOf(['count', 'percentage']).isRequired,
};
